import PropTypes from 'prop-types';
import { memo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Badge, Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import NotificationTrayItem from './NotificationTrayItem';
import useFloatingTrayPosition from './useFloatingTrayPosition';

function NotificationTray({ collapsed, items, onDismissItem, onOpenItem, onToggleCollapsed, selectedOrderId }) {
  const theme = useTheme();
  const { dockToCorner, frameRef, frameStyle, handleDragStart } = useFloatingTrayPosition(collapsed);
  const unseenCount = items.filter((item) => !item.viewed).length;
  const handleCollapse = () => {
    dockToCorner();
    onToggleCollapsed();
  };

  if (collapsed) {
    return (
      <Box ref={frameRef} sx={frameStyle}>
        <Box
          sx={{
            width: 78,
            borderRadius: 4,
            p: 1.25,
            border: '1px solid',
            borderColor: alpha(theme.palette.common.white, 0.18),
            bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.3 : 0.08),
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            boxShadow: `0 22px 50px ${alpha(theme.palette.common.black, 0.18)}`,
          }}
        >
          <Stack spacing={1.25} alignItems="center">
            <Box
              onPointerDown={handleDragStart}
              sx={{
                width: 28,
                height: 5,
                borderRadius: 999,
                bgcolor: alpha(theme.palette.text.primary, 0.18),
                cursor: 'grab',
              }}
            />
            <Badge badgeContent={unseenCount} color="error" max={99}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.success.main, 0.14),
                  color: 'success.main',
                }}
              >
                <Iconify icon="solar:bell-bing-bold" width={22} height={22} />
              </Box>
            </Badge>
            <Tooltip title="Show notifications">
              <IconButton color="primary" onClick={onToggleCollapsed}>
                <Iconify icon="solar:alt-arrow-left-linear" width={18} height={18} />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" align="center" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {items.length} live
            </Typography>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={frameRef} sx={frameStyle}>
      <Box
        sx={{
          width: 340,
          maxWidth: 'calc(100vw - 32px)',
          height: 520,
          borderRadius: 5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: alpha(theme.palette.common.white, 0.18),
          bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.28 : 0.08),
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          boxShadow: `0 28px 70px ${alpha(theme.palette.common.black, 0.22)}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{
            px: 1.75,
            py: 1.5,
            bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.12 : 0.03),
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              onPointerDown={handleDragStart}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: 'text.secondary',
                bgcolor: alpha(theme.palette.common.white, 0.12),
                cursor: 'grab',
              }}
            >
              <Iconify icon="solar:menu-dots-bold" width={16} height={16} />
            </Box>

            <Box>
              <Stack direction="row" spacing={1.8} alignItems="center">
                <Typography variant="subtitle1">Order Trail</Typography>
                <Badge badgeContent={unseenCount} color="error" max={99} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Order notifications
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Hide tray">
              <IconButton size="small" onClick={handleCollapse}>
                <Iconify icon="solar:alt-arrow-right-linear" width={18} height={18} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider />

        <Scrollbar sx={{ height: 'calc(100% - 78px)' }}>
          <Stack spacing={1} sx={{ p: 1.2 }}>
            {items.length ? (
              items.map((item) => (
                <NotificationTrayItem
                  key={item.order_id}
                  item={item}
                  onDismiss={onDismissItem}
                  onOpen={onOpenItem}
                  selected={selectedOrderId === item.order_id}
                />
              ))
            ) : (
              <Box
                sx={{
                  minHeight: 160,
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: alpha(theme.palette.common.white, 0.2),
                  display: 'grid',
                  placeItems: 'center',
                  px: 2,
                  py: 3,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.18 : 0.04),
                }}
              >
                <Stack spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(theme.palette.success.main, 0.14),
                      color: 'success.main',
                    }}
                  >
                    <Iconify icon="solar:bell-off-linear" width={22} height={22} />
                  </Box>
                  <Typography variant="subtitle2">No live order notifications yet</Typography>
                  <Typography variant="caption" color="text.secondary">
                    The tray stays docked here and will populate when the next broadcast arrives.
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </Scrollbar>
      </Box>
    </Box>
  );
}

export default memo(NotificationTray);

NotificationTray.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDismissItem: PropTypes.func.isRequired,
  onOpenItem: PropTypes.func.isRequired,
  onToggleCollapsed: PropTypes.func.isRequired,
  selectedOrderId: PropTypes.string,
};
